import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Tooltip, Typography } from "@material-ui/core";
import { GridColumn } from "./columns";
import { MonitorColumnVisibilityLabels } from "./types";

/**
 * HHMCOVID-5632 (BR-019) — "Customize Columns" dialog.
 *
 * Lists every Monitoring Dashboard column (in table order) with a checkbox to
 * show/hide it. Required columns (configured in app-config.yml, surfaced via
 * meta.required_columns and column.required) cannot be hidden.
 *
 * The rows use native <input type="checkbox"> (not MUI Checkbox) inside a
 * <label><input/><span/></label> — the same markup as FilterBar — so the checkbox
 * colour/appearance matches the Filter dropdown exactly (browser-default accent).
 * Styling is applied inline (not via the sprockets stylesheet) so the layout is
 * self-contained and does not depend on an asset recompile.
 *
 * Accessibility (WCAG 2.1 AA): MUI Dialog provides the focus trap, ESC-to-close
 * and focus restoration. Required columns are NOT rendered with the native
 * `disabled` attribute — that would drop them from the tab order and suppress the
 * tooltip for keyboard/AT users. Instead they use aria-disabled + a no-op change
 * handler (so they stay focusable and checked), are greyed via opacity, get a
 * Tooltip that opens on hover AND focus (1.4.13), an aria-describedby link to the
 * persistent explanation, and a visible "Required" text badge (not colour alone
 * → 1.4.1).
 */

interface CustomizeColumnsDialogProps {
  open: boolean;
  columns: GridColumn[];
  hiddenColumns: string[];
  labels: MonitorColumnVisibilityLabels;
  onClose: () => void;
  onChange: (hiddenColumns: string[]) => void;
}

const TITLE_ID = "monitor-customize-columns-title";
const INTRO_ID = "monitor-customize-columns-intro";

const LIST_STYLE: React.CSSProperties = { listStyle: "none", margin: 0, padding: 0 };
const ROW_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  margin: 0,
  padding: "3px 1px",
  fontWeight: "normal",
  cursor: "pointer"
};
const REQUIRED_ROW_STYLE: React.CSSProperties = { ...ROW_STYLE, cursor: "default" };
const CHECKBOX_STYLE: React.CSSProperties = { margin: "0 0.75rem 0 0", flex: "0 0 auto" };
// Greyed look (desaturate the blue accent + fade) while the control stays
// focusable (a11y — see class comment).
const REQUIRED_CHECKBOX_STYLE: React.CSSProperties = { ...CHECKBOX_STYLE, opacity: 0.6, filter: "grayscale(1)", cursor: "default" };
const REQUIRED_BADGE_STYLE: React.CSSProperties = {
  marginLeft: 6,
  padding: "0 4px",
  fontSize: "0.7rem",
  fontWeight: 600,
  textTransform: "uppercase",
  color: "#555",
  border: "1px solid #bbb",
  borderRadius: 3,
  verticalAlign: "middle"
};

export default function CustomizeColumnsDialog(props: CustomizeColumnsDialogProps) {
  const { open, columns, hiddenColumns, labels, onClose, onChange } = props;
  const hidden = new Set(hiddenColumns);

  const emitHidden = (nextHidden: Set<string>) => {
    // Never persist a required column as hidden, and keep the emitted order stable
    // (registry order) so the payload is deterministic.
    onChange(columns.filter((column) => !column.required && nextHidden.has(column.key)).map((column) => column.key));
  };

  const toggleColumn = (column: GridColumn) => {
    if (column.required) {
      return;
    }
    const next = new Set(hidden);
    if (next.has(column.key)) {
      next.delete(column.key);
    } else {
      next.add(column.key);
    }
    emitHidden(next);
  };

  const showAll = () => onChange([]);

  const renderRequiredRow = (column: GridColumn) => {
    const name = column.menuLabel || column.key;
    return (
      <li key={column.key}>
        <Tooltip title={labels.required_tooltip} arrow>
          <label style={REQUIRED_ROW_STYLE}>
            <input
              type="checkbox"
              checked
              onChange={(event) => event.preventDefault()}
              aria-disabled="true"
              aria-describedby={INTRO_ID}
              style={REQUIRED_CHECKBOX_STYLE}
            />
            <span>
              {name}
              <span style={REQUIRED_BADGE_STYLE}>{labels.required_badge}</span>
            </span>
          </label>
        </Tooltip>
      </li>
    );
  };

  const renderToggleRow = (column: GridColumn) => {
    const name = column.menuLabel || column.key;
    return (
      <li key={column.key}>
        <label style={ROW_STYLE}>
          <input
            type="checkbox"
            checked={!hidden.has(column.key)}
            onChange={() => toggleColumn(column)}
            aria-label={name}
            style={CHECKBOX_STYLE}
          />
          <span>{name}</span>
        </label>
      </li>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={TITLE_ID}
      maxWidth="xs"
      fullWidth
      className="monitor-customize-columns-dialog"
    >
      <DialogTitle id={TITLE_ID} disableTypography>
        <Typography variant="h6" component="h2">
          {labels.dialog_title}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Typography id={INTRO_ID} variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
          {labels.intro}
        </Typography>
        <ul style={LIST_STYLE}>
          {columns.map((column) => (column.required ? renderRequiredRow(column) : renderToggleRow(column)))}
        </ul>
      </DialogContent>
      <DialogActions>
        <Button onClick={showAll}>{labels.show_all}</Button>
        <Button onClick={onClose} color="primary" variant="contained">
          {labels.close}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
